import React, { Component, ErrorInfo } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import './index.css';

/** Catches any error in the app tree so we never show a blank page */
class RootErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Root error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'system-ui', maxWidth: '560px', margin: '0 auto' }}>
          <h1 style={{ color: '#dc2626' }}>Something went wrong</h1>
          <p>{String(this.state.error.message)}</p>
          <pre style={{ background: '#f1f5f9', padding: '1rem', overflow: 'auto', fontSize: '12px' }}>
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

(async function bootstrap() {
  const root = document.getElementById('root');
  if (!root) return;

  try {
    try {
      const { Amplify } = await import('aws-amplify');
      const outputs = await import('../amplify_outputs.json').then((m) => m.default);
      // Cognito custom domain — Google account picker shows auth.tinywins.ai instead of *.amazoncognito.com
      Amplify.configure({
        ...outputs,
        auth: {
          ...outputs.auth,
          oauth: {
            ...outputs.auth.oauth,
            domain: 'auth.tinywins.ai',
          },
        },
      });
      await import('aws-amplify/auth/enable-oauth-listener');
      const { bootstrapSsoTokenHandoff } = await import('./lib/ssoHandoff');
      await bootstrapSsoTokenHandoff();
    } catch (e) {
      console.error('Amplify init failed — auth/API will not work until amplify_outputs.json loads:', e);
    }

    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <RootErrorBoundary>
          <HelmetProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </HelmetProvider>
        </RootErrorBoundary>
      </React.StrictMode>,
    );
  } catch (err) {
    console.error('Bootstrap failed:', err);
    root.innerHTML = `<div style="padding:2rem;font-family:system-ui;max-width:560px;margin:0 auto;">
      <h1 style="color:#dc2626;">Failed to load app</h1>
      <p>${String(err instanceof Error ? err.message : err)}</p>
      <pre style="background:#f1f5f9;padding:1rem;overflow:auto;font-size:12px;">${String(err instanceof Error ? err.stack : err)}</pre>
    </div>`;
  }
})();
